import { isFileDatasourceType } from 'DataSources/datasourcesApi';

/**
 * Maps a platform {@code DatasourceType} string to the masking-service file format(s) the
 * worker would receive. {@code FILE_DELIMITED_FOLDER} can land as either CSV or TSV
 * depending on the stored delimiter; we treat the source as supported if EITHER is
 * present in the capability list (the server-side builder picks the right one).
 *
 * Returns an array of format strings (always uppercased), or `null` if the type is not
 * a file source we have a mapping for. Document/Excel intentionally return `null` here —
 * they're not supported even when the masking service advertises support for them.
 * FILE_GENERIC_FOLDER sources hold per-file sniffed formats, so they're maskable when
 * ANY structured format is enabled — the platform's payload builder gates each file
 * individually by its sniffedType and skips the rest.
 */
function requiredFormatsForFileType(datasourceType) {
  switch (datasourceType) {
    case 'FILE_DELIMITED_FOLDER':
      return ['CSV', 'TSV'];
    case 'FILE_PARQUET_FOLDER':
      return ['PARQUET'];
    case 'FILE_JSON_FOLDER':
      return ['JSON'];
    case 'FILE_XML_FOLDER':
      return ['XML'];
    case 'FILE_FIXED_WIDTH_FOLDER':
      return ['FIXED_WIDTH'];
    case 'FILE_GENERIC_FOLDER':
      return ['CSV', 'TSV', 'PARQUET', 'JSON', 'XML', 'FIXED_WIDTH'];
    default:
      return null;
  }
}

/**
 * For file-source connections: returns {@code true} when the worker's capability list
 * includes at least one of the formats this datasource type can produce. For non-file
 * connections, returns {@code null} so the caller falls back to its JDBC-driver predicate.
 *
 * `supportedFormats` is the lowercased-uppercased list from
 * {@code supportedFileFormatsForRole(capabilities, 'masking' | 'encrypt')}.
 */
export function supportsFileMaskingForConnection(connection, supportedFormats) {
  if (!connection || !isFileDatasourceType(connection.datasourceType)) {
    return null;
  }
  const required = requiredFormatsForFileType(connection.datasourceType);
  if (required == null) {
    return false;
  }
  if (!Array.isArray(supportedFormats) || supportedFormats.length === 0) {
    return false;
  }
  const set = new Set(supportedFormats.map((f) => String(f).trim().toUpperCase()));
  return required.some((f) => set.has(f));
}
